// V2 Title Chain Validator Types
//
// These re-export and extend the API response types from api-responses.ts.
// The shapes are intentionally the same.

import type {
  OwnershipGraphNode as ApiNode,
  OwnershipGraphEdge as ApiEdge,
  OwnershipGraphNodeInterest as ApiInterest,
  OwnershipGraphEncumbrance as ApiEncumbrance,
  OwnershipGraphDocumentRecord,
} from 'types/api-responses';

// ============================================================================
// Re-exported API types (used directly, no transformation needed)
// ============================================================================

export type DocumentRecord = OwnershipGraphDocumentRecord;

// ============================================================================
// Narrowed types — same shape as API but with stricter unions where useful
// ============================================================================

export type EventType = 'E1' | 'E2' | 'E3' | 'E4' | 'E5' | 'E6' | 'E7';

export type PropertyType = 'tract' | 'tract_volume';

// The API types use `string` for event_type and interest_type.
// We keep the same structural shape but components can cast to the
// narrower unions above when they need exhaustive switches.

export type OwnershipNode = ApiNode;
export type ConveyanceEdge = ApiEdge;
export type OwnershipInterest = ApiInterest;
export type InterestEncumbrance = ApiEncumbrance;

// ============================================================================
// Graph & Top-Level Response
// ============================================================================

export interface OwnershipGraph {
  nodes: Record<string, OwnershipNode>;
  edges: Record<string, ConveyanceEdge>;
  adjacency: Record<string, string[]>;
  reverseAdjacency: Record<string, string[]>;
}

export interface PathToReview {
  label: string;
  reason: 'entity_chain' | 'encumbrance_chain';
  subjectNodeId: string;
  nodeIds: string[];
  edgeIds: string[];
  encumbranceType?: string | null;
  encumbranceOwnerNodeId?: string | null;
}

export interface TitleChainData {
  entityId: number;
  graph: OwnershipGraph;
  documents: Record<string, DocumentRecord>;
  subjectAssetNodeIds: (string | number)[];
  pathsToReview?: PathToReview[];
}

// ============================================================================
// Document Detail Data
// ============================================================================

export interface DocumentDetailInterpretation {
  transactions?: Array<{
    id: number;
    grantors: Array<{ name: string | null; entityId?: number }>;
    conveyances: Array<{
      grantee_name: string | null;
      grantee_id: number;
      multiplier?: string;
      interest_type?: string;
    }>;
  }>;
  location_references?: Array<{
    reference_location_name: string | null;
  }>;
}

export interface DocumentDetail {
  document_id: number;
  document_title: string | null;
  azure_blob_path: string | null;
  image_url: string | null;
  source_documents: Array<{
    id: number;
    image_path: string | null;
    has_active_storage_image: boolean;
  }>;
  interpretations: DocumentDetailInterpretation[];
}

export type DocumentDetailMap = Record<string, DocumentDetail>;

export function getDocumentForEdge(
  edge: ConveyanceEdge,
  documents: Record<string, DocumentRecord>
): DocumentRecord | undefined {
  if (edge.documentId === null) return undefined;
  return documents[String(edge.documentId)];
}
